/**
 * Popular Destinations Section Component
 * Displays popular destinations with images, prices, and highlights
 */

import { PopularDestination, PopularDestinationsSection } from '../types/informational';
import { InformationalDataService } from '../services/InformationalDataService';

export class PopularDestinationsSectionComponent implements PopularDestinationsSection {
  public title: string;
  public subtitle: string;
  public destinations: PopularDestination[];
  public showPrices: boolean;
  public maxDestinations: number;
  
  private container: HTMLElement | null = null;
  private dataService: InformationalDataService;

  constructor(
    title: string = 'Popular Destinations',
    subtitle: string = 'Discover our most popular destinations with the best prices',
    maxDestinations: number = 6,
    showPrices: boolean = true
  ) {
    this.title = title;
    this.subtitle = subtitle;
    this.maxDestinations = maxDestinations;
    this.showPrices = showPrices;
    this.dataService = InformationalDataService.getInstance();
    this.destinations = this.dataService.getPopularDestinations().slice(0, maxDestinations);
  }

  /**
   * Initialize the component
   */
  public init(): void {
    this.createContainer();
    this.render();
    this.bindEvents();
  }

  /**
   * Create the container element
   */
  private createContainer(): void {
    // Find insertion point in dynamic sections container
    const dynamicContainer = document.getElementById('dynamic-sections-container');
    if (!dynamicContainer) {
      console.error('PopularDestinations: Dynamic sections container not found');
      return;
    }

    // Create container
    this.container = document.createElement('section');
    this.container.className = 'popular-destinations';
    this.container.id = 'popular-destinations-section';
    this.container.setAttribute('aria-labelledby', 'popular-destinations-title');

    // Append to dynamic container
    dynamicContainer.appendChild(this.container);
  }

  /**
   * Render the component
   */
  private render(): void {
    if (!this.container) return;

    this.container.innerHTML = `
      <div class="popular-destinations__container">
        <header class="popular-destinations__header">
          <h2 class="popular-destinations__title" id="popular-destinations-title">
            ${this.escapeHtml(this.title)}
          </h2>
          <p class="popular-destinations__subtitle">
            ${this.escapeHtml(this.subtitle)}
          </p>
        </header>

        <div class="popular-destinations__grid" role="region" aria-label="Popular destinations list">
          ${this.destinations.map(destination => this.renderDestinationCard(destination)).join('')}
        </div>

        <div class="popular-destinations__actions">
          <button 
            class="btn btn-outline popular-destinations__view-all"
            type="button"
            aria-label="View all available destinations"
          >
            View all destinations
          </button>
        </div>
      </div>
    `;
  }

  /**
   * Render a single destination card
   */
  private renderDestinationCard(destination: PopularDestination): string {
    const priceDisplay = this.showPrices ? `
      <div class="destination-card__price">
        <span class="destination-card__price-label">From</span>
        <span class="destination-card__price-amount">${destination.averagePrice}€</span>
      </div>
    ` : '';

    // Generate fallback image name based on destination name
    const fallbackImageName = destination.name.toLowerCase().replace(/\s+/g, '-');

    return `
      <article class="destination-card" data-destination-id="${destination.id}">
        <div class="destination-card__image">
          <img 
            src="${destination.image}" 
            alt="${this.escapeHtml(destination.name)}, ${this.escapeHtml(destination.country)}"
            class="destination-card__img"
            loading="lazy"
            onerror="this.src='/assets/images/destinations/${fallbackImageName}.jpg'; if(this.onerror) { this.src='/assets/images/destinations/placeholder.jpg'; this.onerror=null; }"
          />
          <div class="destination-card__overlay">
            <div class="destination-card__popularity">
              <span class="destination-card__popularity-icon">⭐</span>
              <span class="destination-card__popularity-score">${destination.popularity}%</span>
            </div>
          </div>
        </div>
        
        <div class="destination-card__content">
          <div class="destination-card__header">
            <h3 class="destination-card__title">
              ${this.escapeHtml(destination.name)}
            </h3>
            <p class="destination-card__country">
              ${this.escapeHtml(destination.country)}
            </p>
          </div>
          
          <p class="destination-card__description">
            ${this.escapeHtml(destination.description)}
          </p>
          
          <div class="destination-card__highlights">
            ${destination.highlights.slice(0, 3).map(highlight => 
              `<span class="destination-card__highlight">${this.escapeHtml(highlight)}</span>`
            ).join('')}
          </div>
          
          <div class="destination-card__footer">
            ${priceDisplay}
            <div class="destination-card__best-time">
              <span class="destination-card__best-time-label">Best period :</span>
              <span class="destination-card__best-time-value">${this.escapeHtml(destination.bestTimeToVisit)}</span>
            </div>
          </div>
          
          <button 
            class="destination-card__cta"
            type="button"
            data-destination-id="${destination.id}"
            aria-label="Rechercher des vols pour ${this.escapeHtml(destination.name)}"
          >
            Find flights
          </button>
        </div>
      </article>
    `;
  }

  /**
   * Bind event listeners
   */
  private bindEvents(): void {
    if (!this.container) return;

    // Handle destination card clicks
    this.container.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const ctaButton = target.closest('.destination-card__cta') as HTMLElement;
      const viewAllButton = target.closest('.popular-destinations__view-all') as HTMLElement;

      if (ctaButton) {
        const destinationId = ctaButton.getAttribute('data-destination-id');
        if (destinationId) {
          this.handleDestinationSearch(destinationId);
        }
      } else if (viewAllButton) {
        this.handleViewAllDestinations();
      }
    });

    // Handle keyboard navigation
    this.container.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        const target = event.target as HTMLElement;
        if (target.classList.contains('destination-card__cta') || 
            target.classList.contains('popular-destinations__view-all')) {
          event.preventDefault();
          target.click();
        }
      }
    });
  }

  /**
   * Handle destination search
   */
  private handleDestinationSearch(destinationId: string): void {
    const destination = this.destinations.find(d => d.id === destinationId);
    if (!destination) return;

    // Fill the search form with destination data
    const destinationInput = document.getElementById('destination') as HTMLInputElement;
    if (destinationInput) {
      destinationInput.value = destination.name;
      destinationInput.focus();
      
      // Scroll to search form
      const heroSection = document.querySelector('.hero');
      if (heroSection) {
        heroSection.scrollIntoView({ behavior: 'smooth' });
      }
    }

    console.log(`Searching flights for ${destination.name}`);
  }

  /**
   * Handle view all destinations
   */
  private handleViewAllDestinations(): void {
    console.log('View all destinations clicked');
    // In a real application, this would navigate to a destinations page
  }

  /**
   * Update destinations data
   */
  public updateDestinations(destinations: PopularDestination[]): void {
    this.destinations = destinations.slice(0, this.maxDestinations);
    this.render();
  }

  /**
   * Escape HTML to prevent XSS
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Destroy the component
   */
  public destroy(): void {
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
  }
}